/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { banIp, getSecuritySummary } from "@/api/requests";
import { showToast } from "@/components/common/Toast";
import { requestAgo } from "@/components/homepage/widgets/useLiveRequests";

/** Hostile-looking external traffic over the last day, with the worst offenders. */
export const SecurityPanel: React.FC<{ onPickIp: (ip: string) => void }> = ({ onPickIp }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const { data } = useQuery({
    queryKey: ["request-security"],
    queryFn: getSecuritySummary,
    refetchInterval: 30_000,
    retry: false,
  });
  const ban = useMutation({
    mutationFn: banIp,
    onSuccess: () => {
      showToast("Address banned", "success");
      qc.invalidateQueries({ queryKey: ["request-security"] });
    },
    onError: (e: Error) => showToast(e.message, "error"),
  });

  if (!data?.enabled) return null;
  const hourly = data.hourly ?? [];
  const peak = Math.max(1, ...hourly);
  const total = (data.failedLogins ?? 0) + (data.bans ?? 0) + (data.drops ?? 0) + (data.probes ?? 0);

  return (
    <section className="rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/[0.03]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <ShieldExclamationIcon className="h-4 w-4 text-amber-500" />
        <span className="text-[13px] font-semibold text-gray-900 dark:text-white">Security · last 24 h</span>
        <span className="ml-auto text-[12px] tabular-nums text-gray-500 dark:text-gray-400">{`${total} suspicious events`}</span>
      </button>
      {open && (
        <div className="grid gap-4 border-t border-gray-100 px-4 py-3 dark:border-white/10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              {[
                ["Failed logins", data.failedLogins],
                ["Banned", data.bans],
                ["Firewall drops", data.drops],
                ["Web probes", data.probes],
              ].map(([label, n]) => (
                <div key={label as string} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-white/10">
                  <div className="text-[18px] font-semibold tabular-nums text-gray-900 dark:text-white">{n ?? 0}</div>
                  <div className="text-gray-500 dark:text-gray-400">{label}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="flex h-12 items-end gap-[2px]" aria-hidden="true">
                {hourly.map((n, i) => (
                  <span
                    key={i}
                    className="flex-1 rounded-sm bg-amber-500/70"
                    style={{ height: `${Math.max(n ? 8 : 2, (n / peak) * 100)}%`, opacity: n ? 1 : 0.25 }}
                  />
                ))}
              </div>
              <div className="mt-1 flex justify-between text-[10.5px] text-gray-400">
                <span>24 h ago</span>
                <span>now</span>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            {(data.top ?? []).length === 0 ? (
              <div className="py-6 text-center text-[12.5px] text-gray-500 dark:text-gray-400">No hostile traffic seen</div>
            ) : (
              <ul className="divide-y divide-gray-100 text-[12.5px] dark:divide-white/10">
                {data.top!.slice(0, 8).map((t) => (
                  <li key={t.ip} className="flex items-center gap-2 py-1.5">
                    <button
                      type="button"
                      className="min-w-0 truncate font-mono text-gray-900 hover:underline dark:text-gray-100"
                      onClick={() => onPickIp(t.ip)}
                      title={t.host ? `${t.ip} · show in the feed` : "Show this address in the feed"}
                    >
                      {t.host || t.ip}
                    </button>
                    {(t.org || t.country) && (
                      <span className="hidden min-w-0 truncate text-[11px] text-gray-400 sm:inline" title={t.org}>
                        {[t.org, t.country].filter(Boolean).join(" · ")}
                      </span>
                    )}
                    <span className="truncate text-[11px] text-gray-400">{t.kinds.join(" · ")}</span>
                    <span className="ml-auto flex-none tabular-nums text-gray-500">{t.count}</span>
                    <span className="flex-none text-[11px] tabular-nums text-gray-400">{requestAgo(t.lastSeen)}</span>
                    {t.banned ? (
                      <span className="flex-none rounded border border-emerald-500/40 px-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">banned</span>
                    ) : data.canBan ? (
                      <button
                        type="button"
                        disabled={ban.isPending}
                        onClick={() => ban.mutate(t.ip)}
                        className="flex-none rounded border border-red-500/40 px-1.5 text-[11px] text-red-600 hover:bg-red-500/10 dark:text-red-400"
                      >
                        Ban
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
